import { Injectable } from '@angular/core';
import { HttpClientService } from '@app/api/api-services/http-client.service';
import { Observable, catchError, map, of } from 'rxjs';
import { CrudService } from '@app/api/api-services/crud.service.base';
import { SiteCallTypesSelectorsApiModel } from '@app/api/api-models/site-settings/site-call-types/SiteCallTypesSelectorsApiModel';
import { SiteCallTypesRequestApiModel } from '@app/api/api-models/site-settings/site-call-types/SiteCallTypesRequestApiModel';
import { SiteCallTypesListApiModel } from '@app/api/api-models/site-settings/site-call-types/SiteCallTypesListApiModel';
import { SiteCallTypesExportRequestApiModel } from '@app/api/api-models/site-settings/site-call-types/SiteCallTypesExportRequestApiModel';
import { SiteCallTypeUsageInfoApiModel } from '@app/api/api-models/site-settings/site-call-types/SiteCallTypeUsageInfoApiModel';
import { SiteCallTypeBaseApiModel } from '@app/api/api-models/site-settings/site-call-types/SiteCallTypeBaseApiModel';

import { SiteCallTypeApiModel, SiteSettingsUsageInfoApiModel } from '@app/api/api-models';
import { toQueryParams } from '@app/shared/utils';
import { SkillsMigrateStatus } from '@app/api/api-models/site-settings/site-call-types/SkillsMigrateStatus';
import { SiteSkillsMigrateRequest } from '@app/api/api-models/site-settings/site-call-types/SiteSkillsMigrateRequest';
import { UploadFileService } from '@shared/services';
import { HttpHeaders } from '@angular/common/http';
import { sanitizeHtml } from '@shared/utils/editor-utils';

export class CallTypeImportResultApiModel {
  public WasProcessed: boolean;
  public FailedHeadingCount: number;
  public FailedCallTypeCount: number;
  public ImportedHeadingCount: number;
  public ImportedCallTypeCount: number;
  public Blob: Blob;
}

@Injectable()
export class CallTypesService extends CrudService<SiteCallTypesListApiModel, SiteCallTypeBaseApiModel, number> {
  public apiUrl = 'site-call-types';

  constructor(public http: HttpClientService) {
    super(http);
  }

  public getAllCallTypes(request?: SiteCallTypesRequestApiModel): Observable<SiteCallTypeApiModel> {
    return this.http.get<SiteCallTypeApiModel>(`${this.apiUrl}`, {
      params: toQueryParams({ GetAll: true, ...request })
    });
  }

  public getCallTypes(request?: SiteCallTypesRequestApiModel): Observable<SiteCallTypeApiModel> {
    return this.http.get<SiteCallTypeApiModel>(`${this.apiUrl}`, { params: toQueryParams(request) });
  }

  public getSelectors(): Observable<SiteCallTypesSelectorsApiModel> {
    return this.http.get<SiteCallTypesSelectorsApiModel>(`${this.apiUrl}/selectors`);
  }

  public add(model: SiteCallTypesListApiModel): Observable<SiteCallTypesListApiModel> {
    model.Description = sanitizeHtml(model.Description);
    return super.add(model);
  }

  public update(model: SiteCallTypesListApiModel): Observable<SiteCallTypesListApiModel> {
    model.Description = sanitizeHtml(model.Description);
    return super.update(model);
  }

  public getPrint(request: SiteCallTypesExportRequestApiModel): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/print`, { params: toQueryParams(request), responseType: 'blob' });
  }

  public getUsageInfo(id: number): Observable<SiteSettingsUsageInfoApiModel> {
    return this.http.get<SiteSettingsUsageInfoApiModel>(`${this.apiUrl}/${id}/usage-info`);
  }

  public getUsage(id: number): Observable<SiteCallTypeUsageInfoApiModel> {
    return this.http.get<SiteCallTypeUsageInfoApiModel>(`${this.apiUrl}/${id}/usage`);
  }

  public migrateCallTypes(payload: SiteSkillsMigrateRequest): Observable<SkillsMigrateStatus> {
    return this.http.put<SkillsMigrateStatus>(`${this.apiUrl}/migrate`, payload);
    // if (_payload.initiate === MigrationRequestEnum.MigrateAll)
    //   return of({ status: MigrationStatusEnum.Initiated }).pipe(delay(2000));
    // return of({ status: MigrationStatusEnum.Cancelled }).pipe(delay(2000));
  }

  public getExistingCallType(payload: SiteCallTypesListApiModel): Observable<SiteCallTypesListApiModel> {
    return this.http.post<SiteCallTypesListApiModel>(`${this.apiUrl}/existing`, payload);
  }

  public getMigrationStatus(): Observable<SkillsMigrateStatus> {
    return this.http.get<SkillsMigrateStatus>(`${this.apiUrl}/status`);
    // return of({ status: MigrationStatusEnum.NotInitiated }).pipe(delay(2000));
  }

  public importCallTypesXlsx(file: File): Observable<CallTypeImportResultApiModel> {
    const uploadFileService = new UploadFileService();

    const payload = new FormData();
    uploadFileService.append(payload, file);

    return this.http
      .post(`${this.apiUrl}/import-xlsx`, payload, {
        responseType: 'blob',
        observe: 'response',
        headers: new HttpHeaders({
          'Skip-Interceptor': '' // custom header
        })
      })
      .pipe(
        map((data: any) => {
          const model = new CallTypeImportResultApiModel();
          model.Blob = data.body;

          if (data.headers.has('Import-Results')) {
            const value = data.headers.get('Import-Results');
            model.WasProcessed = value.split(';')[0] === 'true';
            model.FailedHeadingCount = +value.split(';')[1];
            model.ImportedHeadingCount = +value.split(';')[2];
            model.FailedCallTypeCount = +value.split(';')[3];
            model.ImportedCallTypeCount = +value.split(';')[4];
          }

          return model;
        }),
        catchError((error) => {
          const model = new CallTypeImportResultApiModel();
          if (error.headers.has('Import-Results')) {
            const value = error.headers.get('Import-Results');
            model.WasProcessed = value.split(';')[0] === 'true';
            model.FailedHeadingCount = +value.split(';')[1];
            model.ImportedHeadingCount = +value.split(';')[2];
            model.FailedCallTypeCount = +value.split(';')[3];
            model.ImportedCallTypeCount = +value.split(';')[4];
          }
          return of(model);
        })
      );
  }
}
